import {
    AxiosResponse
} from 'axios';
import {
    bexHttpClient, bexoHttpClient
} from '../../clients';
import {
    ApplicationDetails, ErrorResponse, ToggleApplicationStatusResponse
} from './Types/Bexo.types';

/**
 * Application CRUD has two backends, selected by APP_STUDIO_ENABLED:
 *   - DEV (App Studio enabled)  → BeX Server Redis (`/redis/applications/*`) —
 *     the new app-builder work in development.
 *   - STAGING / PRODUCTION (App Studio disabled) → BeXO (`/api/v2/applications/*`) —
 *     the production source of truth (matches main).
 *
 * Both return the same BeXO-style shape (`app_id`, `tenant_id`, `full_name`, …)
 * the React components consume, so consumers don't change.
 */

// ─── Redis-backed (dev) ──────────────────────────────────────────────────────
interface RedisApp {
    _id: string;
    organizationId: string;
    name: string;
    fullName: string;
    settings?: Record<string, unknown>;
    active: boolean;
    createdAt?: string;
    modifiedAt?: string;
    createdBy?: string;
    modifiedBy?: string;
}

function fromRedis(r: RedisApp & { themeId?: string }): any {
    return {
        app_id: r._id,
        tenant_id: r.organizationId,
        organization_slug: r.organizationId,
        name: r.name,
        full_name: r.fullName,
        settings: r.settings || {},
        active: r.active !== false,
        theme_id: r.themeId ?? null,
        created_at: r.createdAt,
        modified_at: r.modifiedAt,
        created_by: r.createdBy,
        modified_by: r.modifiedBy,
    };
}

class RedisApplicationService {
    async createApplication(data: any): Promise<AxiosResponse> {
        const body = {
            organizationId: data.tenant_id,
            name: data.name || data.full_name,
            fullName: data.full_name,
            settings: data.settings || {},
            active: data.active !== false,
        };
        const res = await bexHttpClient.post('/redis/applications/add-app', body);
        const created = res.data?.data ? fromRedis(res.data.data) : null;
        return { ...res, data: { ...res.data, data: created, app_id: created?.app_id } } as AxiosResponse;
    }

    async listApplication(organizationId: string): Promise<AxiosResponse> {
        const res = await bexHttpClient.get('/redis/applications/list-apps', { params: { organizationId } });
        const list = Array.isArray(res.data) ? res.data.map(fromRedis) : [];
        return { ...res, data: list } as AxiosResponse;
    }

    async getApplication(organizationId: string, appId: string): Promise<ApplicationDetails | ErrorResponse> {
        const res = await bexHttpClient.get('/redis/applications/get-app', { params: { organizationId, _id: appId } });
        if (!res.data || typeof res.data === 'string') return res.data as ErrorResponse;
        return fromRedis(res.data) as ApplicationDetails;
    }

    async updateApplication(organizationId: string, appId: string, data: any): Promise<AxiosResponse> {
        const fullName = data.full_name || data.fullName;
        const body: Record<string, unknown> = { organizationId, _id: appId, name: data.name || fullName, fullName };
        if (data.settings !== undefined) body.settings = data.settings;
        if (data.theme_id !== undefined) body.themeId = data.theme_id;
        else if (data.themeId !== undefined) body.themeId = data.themeId;
        return bexHttpClient.put('/redis/applications/update-app-details', body);
    }

    async deleteApplication(organizationId: string, appId: string): Promise<AxiosResponse> {
        return bexHttpClient.delete('/redis/applications/delete-app', { data: { organizationId, _id: appId } });
    }

    async toggleApplicationStatus(organizationId: string, appId: string, isActive: boolean): Promise<ToggleApplicationStatusResponse> {
        const res = await bexHttpClient.put('/redis/applications/app-status', { organizationId, _id: appId, active: isActive });
        return res.data;
    }
}

// ─── BeXO-backed (staging / production — matches main) ───────────────────────
class BexoApplicationService {
    async createApplication(data: object): Promise<AxiosResponse> {
        return bexoHttpClient.post('/api/v2/applications/', data);
    }

    async listApplication(tenant_id: string): Promise<AxiosResponse> {
        return bexoHttpClient.get(`/api/v2/applications/list/${tenant_id}`, {
            headers: { 'Content-Type': 'application/json' },
        });
    }

    async getApplication(tenant_id: string, appId: string): Promise<ApplicationDetails | ErrorResponse> {
        const response = await bexoHttpClient.get(`/api/v2/applications/list_by_id/${tenant_id}/${appId}`);
        return response.data;
    }

    async updateApplication(tenantName: string, appName: string, data: object): Promise<AxiosResponse> {
        return bexoHttpClient.put(`/api/v2/applications/${tenantName}/${appName}`, data);
    }

    async deleteApplication(tenantName: string, appName: string): Promise<AxiosResponse> {
        return bexoHttpClient.delete(`/api/v2/applications/${tenantName}/${appName}`);
    }

    async toggleApplicationStatus(tenantName: string, appName: string, isActive: boolean): Promise<ToggleApplicationStatusResponse> {
        const response = await bexoHttpClient.put(`/api/v2/applications/status/${tenantName}/${appName}`, null, {
            params: { active: isActive },
        });
        return response.data;
    }
}

// App-CRUD backend is DECOUPLED from APP_STUDIO_ENABLED (which only toggles the
// App Studio UI). The App Builder reads apps from BeXO (bexorchestrator) — the
// production source of truth — everywhere by default. Opt into the Redis-backed
// app-builder explicitly via REACT_APP_APPS_REDIS_BACKEND=true. Enabling the App
// Studio UI must NOT switch this backend.
const applicationService = process.env.REACT_APP_APPS_REDIS_BACKEND === 'true'
    ? new RedisApplicationService()
    : new BexoApplicationService();

export { applicationService };
